"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { FormEvent, useEffect, useState } from "react";
import { apiFetch, clearAuthToken, getAuthToken, setAuthToken } from "../lib/api";
import AccountMenu from "./AccountMenu";
import HelpButton from "./HelpButton";

type User = { id: number; email: string };

function EmberMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 2c-1.2 2.8-4.3 5.1-4.3 9.2A5.3 5.3 0 0012.5 17a4.7 4.7 0 004.8-4.8c0-1.5-.5-2.8-1.4-3.9.4 2.1-.6 3.3-1.7 3.3-1.3 0-1.9-1.3-1.1-2.8C14.5 6.6 13 4 12 2z" />
    </svg>
  );
}

// Reachable without signing in — e.g. an email-verification link opened in a
// fresh tab/browser with no session yet.
const PUBLIC_PATHS = ["/verify-email"];

export default function AuthGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [user, setUser] = useState<User | null>(null);
  const [checked, setChecked] = useState(false);
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [justRegistered, setJustRegistered] = useState(false);

  const loadSession = async () => {
    if (!getAuthToken()) {
      setChecked(true);
      return;
    }
    try {
      const response = await apiFetch("/auth/me");
      if (response.ok) setUser((await response.json()).user);
    } finally {
      setChecked(true);
    }
  };

  useEffect(() => {
    loadSession();
    const showLogin = () => { setUser(null); setChecked(true); };
    window.addEventListener("dynasty-auth-required", showLogin);
    return () => window.removeEventListener("dynasty-auth-required", showLogin);
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await apiFetch(`/auth/${mode === "login" ? "login" : "register"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Unable to continue.");
        return;
      }
      setAuthToken(data.token);
      // Both are private and can differ between accounts sharing this
      // browser, so do not carry a previous account's selections into this
      // session — a fresh/other account should never inherit someone else's
      // active ranking list or draft.
      localStorage.removeItem("dynasty_active_rankings");
      localStorage.removeItem("dynasty_active_draft");
      setJustRegistered(mode === "register");
      setUser(data.user);
      setPassword("");
    } catch {
      setError("Unable to reach the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (PUBLIC_PATHS.includes(pathname)) return <>{children}</>;

  if (!checked) return <div className="min-h-screen bg-zinc-900" />;

  if (!user) {
    return (
      <main className="min-h-screen bg-zinc-900 text-zinc-200 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-md bg-zinc-800 border border-zinc-700 rounded-xl p-8 shadow-2xl">
          <div className="flex items-center gap-2 text-2xl font-bold text-orange-400 mb-2">
            <EmberMark className="w-6 h-6" />
            Fantasy Ranker<span className="text-zinc-500"> PRO</span>
          </div>
          <h1 className="text-xl font-semibold mb-2">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="text-sm text-zinc-400 mb-6">Private fantasy football rankings for redraft and dynasty leagues. Save up to two lists.</p>
          <label className="block text-sm font-medium mb-2">Email</label>
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-zinc-950 border border-zinc-600 rounded px-3 py-2.5 mb-4 focus:outline-none focus:border-orange-400" />
          <label className="block text-sm font-medium mb-2">Password</label>
          <input required minLength={8} type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-zinc-950 border border-zinc-600 rounded px-3 py-2.5 focus:outline-none focus:border-orange-400" />
          {mode === "register" && <p className="text-xs text-zinc-400 mt-2">Use at least 8 characters.</p>}
          {error && <p className="text-sm text-red-400 mt-4">{error}</p>}
          <button disabled={submitting} className="w-full mt-6 py-3 rounded bg-orange-600 hover:bg-orange-500 disabled:opacity-50 font-bold transition-colors">
            {submitting ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
          <button type="button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="w-full mt-4 text-sm text-orange-400 hover:text-orange-300">
            {mode === "login" ? "Need an account? Create one" : "Already have an account? Sign in"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <>
      <nav className="bg-zinc-950 border-b border-zinc-800 px-2 sm:px-4 py-3 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex flex-nowrap items-center justify-between gap-1 sm:gap-6">
          <div className="flex items-center gap-1.5 sm:gap-2 text-sm sm:text-xl font-bold text-orange-400 tracking-wide shrink-0 whitespace-nowrap">
            <EmberMark className="w-4 h-4 sm:w-6 sm:h-6 shrink-0" />
            Fantasy Ranker<span className="text-zinc-500 hidden sm:inline"> PRO</span>
          </div>
          <div className="flex flex-nowrap items-center gap-0.5 sm:gap-3">
            <Link href="/rankings" className="px-1.5 sm:px-3 py-1.5 rounded-md text-zinc-300 hover:text-amber-400 hover:bg-zinc-800 font-semibold transition-colors text-[11px] sm:text-base whitespace-nowrap">
              📋 <span className="hidden sm:inline">My Fantasy Rankings</span><span className="sm:hidden">Ranks</span>
            </Link>
            <Link href="/draft" className="px-1.5 sm:px-3 py-1.5 rounded-md text-zinc-300 hover:text-orange-400 hover:bg-zinc-800 font-semibold transition-colors text-[11px] sm:text-base whitespace-nowrap">
              🏈 <span className="hidden sm:inline">Draft Assistant</span><span className="sm:hidden">Draft</span>
            </Link>
            <HelpButton />
            <AccountMenu
              email={user.email}
              onSignOut={() => { clearAuthToken(); localStorage.removeItem("dynasty_active_rankings"); localStorage.removeItem("dynasty_active_draft"); setUser(null); }}
            />
          </div>
        </div>
      </nav>
      {justRegistered && (
        <div className="max-w-7xl mx-auto mt-6 px-2 sm:px-4 flex items-start justify-between gap-3 bg-amber-950/30 border border-amber-500/40 rounded-lg p-4">
          <p className="text-sm text-amber-200">
            Welcome! We sent a verification link to {user.email} — check your inbox (and spam folder). You can keep using the app in the meantime; verification status is in Settings.
          </p>
          <button onClick={() => setJustRegistered(false)} className="text-amber-400 hover:text-amber-200 text-lg leading-none shrink-0" aria-label="Dismiss">×</button>
        </div>
      )}
      <div className="pt-6">{children}</div>
    </>
  );
}
